import * as THREE from 'three';

export const GEAR_TEETH = 10;
export const GEAR_OUTER_RADIUS = 0.45;
export const GEAR_INNER_RADIUS = 0.34;
export const GEAR_EXTRUDE_DEPTH = 0.12;
export const CHAIN_LINKS_PER_CELL = 4;
export const RAIL_RADIUS = 0.035;
export const ENDPOINT_SPHERE_RADIUS = 0.2;
export const ENDPOINT_RIM_MAJOR = 0.3;
export const ENDPOINT_RIM_TUBE = 0.035;

export function createGearShape(
  outerR = GEAR_OUTER_RADIUS,
  innerR = GEAR_INNER_RADIUS,
  teeth = GEAR_TEETH,
): THREE.Shape {
  const shape = new THREE.Shape();
  for (let i = 0; i < teeth * 2; i++) {
    const angle = (i * Math.PI) / teeth;
    const r = i % 2 === 0 ? outerR : innerR;
    const x = Math.cos(angle) * r;
    const y = Math.sin(angle) * r;
    if (i === 0) shape.moveTo(x, y);
    else shape.lineTo(x, y);
  }
  shape.closePath();

  const holeR = innerR * 0.14;
  for (let h = 0; h < 4; h++) {
    const angle = (h * Math.PI) / 2 + Math.PI / 4;
    const hx = Math.cos(angle) * innerR * 0.55;
    const hy = Math.sin(angle) * innerR * 0.55;
    const hole = new THREE.Path();
    hole.absarc(hx, hy, holeR, 0, Math.PI * 2, false);
    shape.holes.push(hole);
  }

  return shape;
}

let cachedGearGeometry: THREE.ExtrudeGeometry | null = null;

export function createExtrudedGearGeometry(): THREE.ExtrudeGeometry {
  if (cachedGearGeometry) return cachedGearGeometry;

  const shape = createGearShape();
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: GEAR_EXTRUDE_DEPTH,
    bevelEnabled: true,
    bevelSize: 0.012,
    bevelThickness: 0.012,
    bevelSegments: 2,
  });
  geo.rotateX(-Math.PI / 2);
  geo.translate(0, GEAR_EXTRUDE_DEPTH / 2, 0);
  cachedGearGeometry = geo;
  return geo;
}

let cachedChainLinkGeometry: THREE.TorusGeometry | null = null;

export function createChainLinkGeometry(): THREE.TorusGeometry {
  if (cachedChainLinkGeometry) return cachedChainLinkGeometry;
  cachedChainLinkGeometry = new THREE.TorusGeometry(0.055, 0.022, 6, 10);
  return cachedChainLinkGeometry;
}

let cachedEndpointRimGeometry: THREE.TorusGeometry | null = null;

export function createEndpointRimGeometry(): THREE.TorusGeometry {
  if (cachedEndpointRimGeometry) return cachedEndpointRimGeometry;
  cachedEndpointRimGeometry = new THREE.TorusGeometry(
    ENDPOINT_RIM_MAJOR,
    ENDPOINT_RIM_TUBE,
    10,
    24,
  );
  return cachedEndpointRimGeometry;
}

export function chainLinkMatrix(
  start: THREE.Vector3,
  end: THREE.Vector3,
  t: number,
  target: THREE.Matrix4,
): void {
  const pos = start.clone().lerp(end, t);
  const dir = end.clone().sub(start).normalize();
  const holeAxis = new THREE.Vector3(0, 0, 1);
  const quat = new THREE.Quaternion().setFromUnitVectors(holeAxis, dir);
  target.compose(pos, quat, new THREE.Vector3(1, 1, 1));
}

export function disposeGearGeometryCache(): void {
  cachedGearGeometry?.dispose();
  cachedGearGeometry = null;
  cachedChainLinkGeometry?.dispose();
  cachedChainLinkGeometry = null;
  cachedEndpointRimGeometry?.dispose();
  cachedEndpointRimGeometry = null;
}
